"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DISPATCHED_STATUSES = exports.editRequestSchema = exports.editChangesSchema = exports.HUB_MANAGER_APPROVAL_AFTER_MINUTES = exports.ALWAYS_APPROVED_FIELDS = exports.URGENT_FIELDS = exports.EDIT_FIELDS = exports.moveSchema = exports.bulkLookupSchema = exports.shipmentListSchema = exports.exportRequestSchema = exports.shipmentFilterSchema = void 0;
exports.splitPasted = splitPasted;
// Shipment list filters, bulk search and actions (plan 4.3). Used by the API, the export
// job and the admin console.
const zod_1 = require("zod");
const status_1 = require("./status");
const day = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'DATE_FORMAT');
const csv = (item) => zod_1.z.preprocess((v) => (typeof v === 'string' ? v.split(',').filter(Boolean) : v), zod_1.z.array(item).max(50));
/** Filters of the shipment list (plan 4.3): project, merchant, district, hub, courier, team leader, status, date. */
exports.shipmentFilterSchema = zod_1.z.object({
    projectId: zod_1.z.uuid().optional(),
    merchantId: zod_1.z.uuid().optional(),
    districtId: zod_1.z.uuid().optional(),
    hubId: zod_1.z.uuid().optional(),
    courierId: zod_1.z.uuid().optional(),
    /** the dispatcher / team leader the courier was mapped to on the assignment day (D-0020) */
    leaderId: zod_1.z.uuid().optional(),
    status: csv(zod_1.z.enum(status_1.SHIPMENT_STATUSES)).optional(),
    /** upload date range, UAE dates */
    from: day.optional(),
    to: day.optional(),
    /** one AWB, merchant reference or mobile */
    q: zod_1.z.string().trim().min(1).max(100).optional(),
    /** only parcels whose address needs fixing */
    addressIssue: zod_1.z.coerce.boolean().optional(),
    /** only parcels the courier did not load (reported missing, or left when the route started; P3-6) */
    loadMissing: zod_1.z.coerce.boolean().optional(),
});
/**
 * An export: the list filter plus the layout. "client" = the client status file for one project
 * (D-0020): AWB, status, failure reason, POD and the courier's client ID on that day.
 */
exports.exportRequestSchema = exports.shipmentFilterSchema
    .extend({ layout: zod_1.z.enum(['standard', 'client']).default('standard') })
    .refine((v) => v.layout !== 'client' || !!v.projectId, { message: 'CHOOSE_PROJECT', path: ['projectId'] });
exports.shipmentListSchema = exports.shipmentFilterSchema.extend({
    page: zod_1.z.coerce.number().int().min(1).max(10_000).default(1),
    pageSize: zod_1.z.coerce.number().int().min(1).max(100).default(50),
});
/** Paste many AWBs or merchant references (one per line, comma or space). */
exports.bulkLookupSchema = zod_1.z.object({
    text: zod_1.z.string().max(200_000),
});
/** Turns pasted text into distinct values, upper-cased, at most 2,000. */
function splitPasted(text) {
    const seen = new Set();
    for (const raw of text.split(/[\s,;]+/)) {
        const v = raw.trim().toUpperCase();
        if (v)
            seen.add(v);
        if (seen.size >= 2000)
            break;
    }
    return [...seen];
}
/** One status move for one or many parcels. */
exports.moveSchema = zod_1.z.object({
    shipmentIds: zod_1.z.array(zod_1.z.uuid()).min(1).max(2000),
    to: zod_1.z.enum(status_1.SHIPMENT_STATUSES),
    reason: zod_1.z.string().trim().max(500).nullish(),
    courierId: zod_1.z.uuid().nullish(),
    date: day.nullish(),
    toHubId: zod_1.z.uuid().nullish(),
    failureReasonId: zod_1.z.uuid().nullish(),
});
// ---------------------------------------------------------------- edits (plan 4.3, P2-2, P2-3)
/** Fields that may change on a shipment. The AWB never changes. */
exports.EDIT_FIELDS = ['consigneeName', 'phone', 'altPhone', 'addressText', 'city', 'districtId', 'pin', 'codFils', 'pieceCount', 'weightGrams'];
/** A team leader may apply these at once after dispatch, without approval ("urgent fix"). */
exports.URGENT_FIELDS = ['addressText', 'city', 'districtId', 'pin', 'altPhone'];
/** Always need approval, even before dispatch. */
exports.ALWAYS_APPROVED_FIELDS = ['codFils'];
/** After this long without an answer, the hub manager may approve instead of the Ops manager. */
exports.HUB_MANAGER_APPROVAL_AFTER_MINUTES = 30;
exports.editChangesSchema = zod_1.z
    .object({
    consigneeName: zod_1.z.string().trim().min(2).max(200),
    phone: zod_1.z.string().trim().min(7).max(30),
    altPhone: zod_1.z.string().trim().min(7).max(30).nullable(),
    addressText: zod_1.z.string().trim().min(3).max(500),
    city: zod_1.z.string().trim().max(100).nullable(),
    districtId: zod_1.z.uuid(),
    pin: zod_1.z.object({ latitude: zod_1.z.number().min(22).max(27), longitude: zod_1.z.number().min(51).max(57) }),
    codFils: zod_1.z.number().int().min(0).max(100_000_00),
    pieceCount: zod_1.z.number().int().min(1).max(99),
    weightGrams: zod_1.z.number().int().min(1).max(500_000).nullable(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, 'NOTHING_TO_CHANGE');
exports.editRequestSchema = zod_1.z.object({
    changes: exports.editChangesSchema,
    reason: zod_1.z.string().trim().min(3).max(500),
    /** team leader: apply an address / pin / alternate-phone fix at once */
    urgent: zod_1.z.boolean().default(false),
});
/** Statuses after dispatch (P2-2: from Assigned onwards, until the parcel is closed). */
exports.DISPATCHED_STATUSES = ['ASSIGNED', 'OUT_FOR_DELIVERY', 'IN_TRANSIT', 'FAILED', 'RESCHEDULED', 'PARTIALLY_DELIVERED'];
//# sourceMappingURL=shipments.js.map